// src/components/character/HeaderImageCropper.tsx
import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface HeaderImageCropperProps {
  imageUrl: string;
  isOpen: boolean;
  onCancel: () => void;
  onSave: (file: File) => Promise<void>;
  isUploading?: boolean;
}

const HeaderImageCropper: React.FC<HeaderImageCropperProps> = ({
  imageUrl,
  isOpen,
  onCancel,
  onSave,
  isUploading = false
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 33.33, // 3:1 aspect ratio for header (100/3 = 33.33)
    x: 0,
    y: 33.33 // Center vertically
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // モーダルが開いたときにボディスクロールを無効化
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) return;

    setIsProcessing(true);
    setError(null);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('No 2d context');
      }

      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Set canvas size to desired output size (1500x500 for header)
      canvas.width = 1500;
      canvas.height = 500;

      // Calculate source dimensions
      const sourceX = completedCrop.x * scaleX;
      const sourceY = completedCrop.y * scaleY;
      const sourceWidth = completedCrop.width * scaleX;
      const sourceHeight = completedCrop.height * scaleY;

      // Enable image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the cropped image
      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height
      );

      // Convert canvas to blob
      await new Promise<void>((resolve, reject) => {
        canvas.toBlob(
          async (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }

            // Create File object
            const file = new File([blob], 'header-image.jpg', {
              type: 'image/jpeg'
            });

            try {
              await onSave(file);
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          'image/jpeg',
          0.95
        );
      });
    } catch (error) {
      console.error('Error processing header image:', error);
      setError('画像の処理中にエラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-4 md:p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-gray-900">
          ヘッダー画像を調整
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          ヘッダー画像は3:1の比率で表示されます。選択範囲をドラッグして調整してください。
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="mb-4 bg-gray-100 rounded-lg p-2">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={3} // 3:1 aspect ratio for header
            keepSelection
          >
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Header"
              style={{ maxHeight: '400px', width: 'auto' }}
              className="mx-auto"
            />
          </ReactCrop>
        </div>

        {/* Hidden canvas for cropping */}
        <canvas
          ref={canvasRef}
          className="hidden"
        />

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isProcessing || isUploading}
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-full font-medium hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={isProcessing || isUploading || !completedCrop}
            className="px-6 py-2.5 bg-purple-500 text-white rounded-full font-medium hover:bg-purple-600 disabled:opacity-50 transition-colors"
          >
            {isProcessing || isUploading ? '処理中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeaderImageCropper;
